import { dateAdd, firstDayOfMonth, getToday, lastDayOfMonth } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { IconName } from "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import "./date-picker";
import type { DatePicker } from "./date-picker";
import "./popover";
import type { Popover } from "./popover";

@customElement("ptc-date-input")
export class DateInput extends LitElement {
    readonly type = "date";

    @property()
    name: string;

    @property()
    className: string;

    @property({ type: Boolean, reflect: true })
    disabled: boolean;

    @property({ type: Boolean, reflect: true })
    readonly: boolean;

    @property()
    icon: IconName;

    @property({ attribute: false })
    min?: DateString;

    @property({ attribute: false })
    max?: DateString;

    @property({ type: Boolean })
    required: boolean;

    @property({ attribute: "disable-year", type: Boolean })
    disableYear = false;

    @property({ attribute: "disable-month", type: Boolean })
    disableMonth = false;

    @property()
    datePicker: "inline" | "popover" | "none" = "none";

    @property()
    datePickerBadge?: DatePicker["badge"];

    @property()
    displayRangeWith?: string;

    @property({ attribute: false })
    get value(): DateString | null {
        return (this._valueInput && (this._valueInput.value as DateString)) || null;
    }

    private _popoverElementRef: Ref<Popover> = createRef();

    set value(val: DateString | null) {
        void (async () => {
            if (!this._valueInput) {
                await this.updateComplete;
            }

            if (val) {
                const [year, month, day] = val.split("-");
                this._dayInput.value = day;
                this._monthInput.value = month;
                this._yearInput.value = year;
            } else {
                this._dayInput.value = this._monthInput.value = this._yearInput.value = "";
            }

            this._lastValue = val;
            this._updateValue();
        })();
    }

    @property({ attribute: false })
    defaultValue: DateString | "" = "";

    @property({ type: Number })
    get day() {
        if (!this._dayInput || !this._dayInput.value) {
            return null;
        }
        const day = Number(this._dayInput.value);
        return isNaN(day) ? null : day;
    }
    set day(day: number | null) {
        this._dayInput.value = day === null ? "" : day.toString().padStart(2, "0");
    }

    @property({ type: Number })
    get month() {
        if (!this._monthInput || !this._monthInput.value) {
            return null;
        }
        const month = Number(this._monthInput.value);
        return isNaN(month) ? null : month;
    }
    set month(month: number | null) {
        this._monthInput.value = month === null ? "" : month.toString().padStart(2, "0");
    }

    @property({ type: Number })
    get year() {
        if (!this._yearInput || !this._yearInput.value) {
            return null;
        }
        const year = Number(this._yearInput.value);
        return isNaN(year) ? null : year;
    }
    set year(year: number | null) {
        this._yearInput.value = year === null ? "" : year.toString().padStart(2, "0");
    }

    @query("input[type='date']")
    private _valueInput: HTMLInputElement;

    @query("input.date-input-day")
    private _dayInput: HTMLInputElement;

    @query("input.date-input-month")
    private _monthInput: HTMLInputElement;

    @query("input.date-input-year")
    private _yearInput: HTMLInputElement;

    @query("ptc-date-picker")
    private _datePicker: DatePicker;

    private get _maxDay() {
        if (!this.year || !this.month) {
            return 31;
        }
        return new Date(this.year, this.month, 0).getDate();
    }

    private _clickHandler = (e: Event) => {
        if (!this._dayInput.value) {
            this._dayInput.focus();
            e.preventDefault();
        }
    };

    private _changeTimeout: ReturnType<typeof setTimeout>;

    private _blurHandler = () => {
        if (typeof this._lastValue === "undefined" || this._lastValue !== this.value) {
            this.dispatchEvent(new CustomEvent("change", { bubbles: true, composed: true }));
            queueMicrotask(() => {
                this._lastValue = this.value;
            });
        }
    };

    private _focusHandler = () => {
        clearTimeout(this._changeTimeout);
    };

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("click", this._clickHandler);
        this.addEventListener("focusin", this._focusHandler);
        this.addEventListener("focusout", this._blurHandler);
    }

    disconnectedCallback() {
        super.connectedCallback();
        this.removeEventListener("click", this._clickHandler);
        this.removeEventListener("focusin", this._focusHandler);
        this.removeEventListener("focusout", this._blurHandler);
    }

    focus() {
        return this.focusDay();
    }

    private _getDatePart(date: DateString | "", part: "day" | "month" | "year") {
        if (!date?.length) return "";
        const parts = date.split("-");
        switch (part) {
            case "day":
                return parts[2];
            case "month":
                return parts[1];
            case "year":
                return parts[0];
        }
    }

    async focusDay() {
        if (!this._dayInput) {
            await this.updateComplete;
        }
        this._dayInput.focus();
    }

    async focusMonth() {
        if (!this._monthInput) {
            await this.updateComplete;
        }
        this._monthInput.focus();
    }

    async focusYear() {
        if (!this._yearInput) {
            await this.updateComplete;
        }
        this._yearInput.focus();
    }

    blur() {
        this._dayInput.blur();
        this._monthInput.blur();
        this._yearInput.blur();
    }

    setCustomValidity(val: string) {
        this._valueInput.setCustomValidity(val);
    }

    reportValidity() {
        return this._valueInput.reportValidity();
    }

    private _blurTimeout: number;
    private _lastValue?: string | null;

    private _dayChanged(e: InputEvent) {
        e.stopPropagation();

        // Clamp day to valid range (truncate to first digit if exceeding max)
        if (this.day !== null && this.day > this._maxDay) {
            this._dayInput.value = this._dayInput.value.slice(0, 1);
        }

        // auto advance to month input
        if (this.day !== null && (this.day > 3 || this._dayInput.value.length > 1)) {
            this._monthInput.focus();
        }

        this._updateValue();
        this.dispatchEvent(new CustomEvent("input"));
    }

    private _monthChanged(e: InputEvent) {
        e.stopPropagation();

        // Clamp month to valid range (truncate to first digit if exceeding 12)

        // Adjust day if it exceeds the new month's max days
        if (this.day !== null && this.day > this._maxDay) {
            this.day = this._maxDay;
        }

        if (this.month !== null && this.month > 12) {
            this._monthInput.value = this._monthInput.value.slice(0, 1);
        }

        // auto advance to year input
        if (this.month !== null && (this.month > 1 || this._monthInput.value.length > 1)) {
            this._yearInput.focus();
        }

        this._updateValue();
        this.dispatchEvent(new CustomEvent("input"));
    }

    private _yearChanged(e: InputEvent) {
        e.stopPropagation();

        if ((this.year !== null && this.year > 3000) || this._yearInput.value.length > 4) {
            this._yearInput.value = this._yearInput.value.slice(0, 4);
        }

        if (this.day !== null && this.day > this._maxDay) {
            this.day = this._maxDay;
        }

        this._updateValue();

        this.dispatchEvent(new CustomEvent("input"));
    }

    private _updateValue() {
        const year = this.year;
        const value =
            this.day === null || this.month === null || year === null || year < 1900 || year > 2100
                ? null
                : (`${year.toString().padStart(4, "0")}-${this.month.toString().padStart(2, "0")}-${this.day
                      .toString()
                      .padStart(2, "0")}` as DateString);
        this._valueInput.value = value || "";
        if (this._datePicker) {
            this._datePicker.value = value;
        }
    }

    private _focus(e: FocusEvent) {
        const input = e.target as HTMLInputElement;

        try {
            input.select();
        } catch (e) {
            input.setSelectionRange(0, input.value.length);
        }

        this.classList.add("focus");
        window.clearTimeout(this._blurTimeout);
    }

    private _blur(e: Event) {
        e.stopPropagation();

        const input = e.target as HTMLInputElement;
        const isEmptyOrInvalid = input.value === "" || isNaN(Number(input.value));

        if (isEmptyOrInvalid) {
            input.value = "";
            return;
        }

        if (input === this._yearInput) {
            // Pad year with century: 51-99 -> 1951-1999, 00-50 -> 2000-2050
            input.value = input.value.padStart(4, Number(input.value) > 50 ? "1900" : "2000");
        } else {
            // Pad day/month with leading zero
            input.value = input.value.padStart(2, "0");
        }

        this._updateValue();

        this.dispatchEvent(new CustomEvent("input"));

        this.classList.remove("focus");

        this._blurTimeout = window.setTimeout(() => this.dispatchEvent(new CustomEvent("blur")), 100);
    }

    private _mouseUp(ev: MouseEvent) {
        ev.preventDefault();
        this._focus(ev);
    }

    private _keydown(e: KeyboardEvent) {
        const input = e.target as HTMLInputElement;

        // Day input: ArrowUp/ArrowDown to cycle through days
        if (input === this._dayInput) {
            if (e.key === "ArrowUp") {
                if (e.shiftKey) {
                    // Shift+ArrowUp: jump to last day of month
                    const lastMonthDay = lastDayOfMonth(this.value || getToday());
                    if (lastMonthDay) {
                        this.value && (this.value = dateAdd(lastMonthDay, { days: -1 }));
                    }
                } else if (this.value) {
                    // ArrowUp: increment day, cycle to next month if exceeding max
                    const newDay = (this.day || 0) + 1;
                    if (newDay > this._maxDay) {
                        this.value = dateAdd(this.value, { days: 1 });
                    } else {
                        this.day = newDay;
                        this._updateValue();
                        this.dispatchEvent(new CustomEvent("input"));
                    }
                }
                e.preventDefault();
            }
            if (e.key === "ArrowDown") {
                if (e.shiftKey) {
                    // Shift+ArrowDown: jump to first day of month
                    const firstMonthDay = firstDayOfMonth(this.value || getToday());
                    if (firstMonthDay) {
                        this.value && (this.value = dateAdd(firstMonthDay, { days: -1 }));
                    }
                } else if (this.value) {
                    // ArrowDown: decrement day, cycle to previous month if below 1
                    const newDay = (this.day || 1) - 1;
                    if (newDay < 1) {
                        this.value = dateAdd(this.value, { days: -1 });
                    } else {
                        this.day = newDay;
                        this._updateValue();
                        this.dispatchEvent(new CustomEvent("input"));
                    }
                }
                e.preventDefault();
            }
        }

        // Month input: ArrowUp/ArrowDown to cycle through months
        if (input === this._monthInput) {
            if (e.key === "ArrowUp" && this.value) {
                const newMonth = (this.month || 0) + 1;
                if (newMonth > 12) {
                    this.value = dateAdd(this.value, { months: 1 });
                } else {
                    this.month = newMonth;
                    // Adjust day if it exceeds the new month's max
                    if (this.day && this.day > this._maxDay) {
                        this.day = this._maxDay;
                    }
                    this._updateValue();
                    this.dispatchEvent(new CustomEvent("input"));
                }
                e.preventDefault();
            }
            if (e.key === "ArrowDown" && this.value) {
                const newMonth = (this.month || 1) - 1;
                if (newMonth < 1) {
                    this.value = dateAdd(this.value, { months: -1 });
                } else {
                    this.month = newMonth;
                    // Adjust day if it exceeds the new month's max
                    if (this.day && this.day > this._maxDay) {
                        this.day = this._maxDay;
                    }
                    this._updateValue();
                    this.dispatchEvent(new CustomEvent("input"));
                }
                e.preventDefault();
            }
        }

        if ((e.key === "ArrowUp" || e.key === "ArrowDown") && input === this._yearInput) {
            if (!this._yearInput.value?.length) {
                this._yearInput.value = String(new Date().getFullYear());
            }
        }
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            setTimeout(() => {
                input.select();
            });
        }

        // Navigate to previous input
        if (["ArrowLeft"].includes(e.key)) {
            if (input === this._yearInput) {
                this._monthInput.focus();
                e.preventDefault();
            }

            if (input === this._monthInput || input === this._dayInput) {
                this._dayInput.focus();
                e.preventDefault();
            }
        }

        // Navigate to next input
        if (["ArrowRight", ".", ","].includes(e.key)) {
            if (input === this._dayInput) {
                this._monthInput.focus();
                e.preventDefault();
            }

            if (input === this._monthInput || input === this._yearInput) {
                this._yearInput.focus();
                e.preventDefault();
            }
        }

        if (["e", ".", ",", "-"].includes(e.key)) {
            e.preventDefault();
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            this._popoverElementRef.value?.hide();
            this.dispatchEvent(new CustomEvent("change", { bubbles: true, composed: true }));
            this.dispatchEvent(new CustomEvent("enter"));
        }

        if (e.key === "Escape") {
            e.preventDefault();
            this._popoverElementRef.value?.hide();
            this.dispatchEvent(new CustomEvent("change", { bubbles: true, composed: true }));
            this.dispatchEvent(new CustomEvent("escape"));
        }
    }

    private _datePicked(e: CustomEvent<{ value: DateString }>) {
        this.value = e.detail.value;
        this.dispatchEvent(new CustomEvent("input"));
        this.dispatchEvent(new CustomEvent("change", { bubbles: true, composed: true }));
    }

    static styles = css`
        ptc-date-input {
            display: inline-block;
            border-radius: var(--border-radius);
            border: solid 1px var(--shade-2);
            background: var(--color-bg);
            position: relative;
        }

        ptc-date-input .date-input-inner {
            padding: var(--date-input-padding, 0.6em);
        }

        ptc-date-input.focus {
            border-color: var(--color-primary);
        }

        ptc-date-input input {
            padding: 0;
            border: none;
            background: transparent;
        }

        ptc-date-input .date-input-day {
            text-align: right;
            width: 1.4em;
        }

        ptc-date-input .date-input-month {
            text-align: center;
            width: 1.4em;
        }

        ptc-date-input .date-input-year {
            text-align: left;
            width: 2.6em;
        }

        ptc-date-input ptc-ui-icon {
            position: relative;
            top: 1px;
        }

        ptc-date-input input[type="date"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
            pointer-events: none;
        }

        ptc-date-input .checkbox-sized {
            padding: 0.8em;
            line-height: calc(
                23 / 14
            ); // raise line-height to match checkbox size (checkbox 23px height, date input 14px font-size)
        }
    `;

    createRenderRoot() {
        return this;
    }

    render() {
        return html`
            <div class="${`centering horizontal layout date-input-inner${this.className ? ` ${this.className}` : ""}`}">
                ${this.icon ? html` <ptc-ui-icon name="${this.icon}"></ptc-ui-icon> ` : ""}

                <input
                    type="number"
                    min="1"
                    max=${this._maxDay}
                    pattern="[0-9]*"
                    inputmode="numeric"
                    maxlength="2"
                    class="date-input-day"
                    placeholder=" -- "
                    @input=${this._dayChanged}
                    @focus=${this._focus}
                    @click=${this._focus}
                    @mouseup=${this._mouseUp}
                    @blur=${this._blur}
                    @keydown=${this._keydown}
                    @change=${(e: Event) => e.stopPropagation()}
                    .defaultValue=${this._getDatePart(this.defaultValue, "day")}
                    ?disabled=${this.disabled}
                    ?readonly=${this.readonly}
                />

                <div>.</div>

                <input
                    type="number"
                    min="1"
                    max="12"
                    pattern="[0-9]*"
                    inputmode="numeric"
                    maxlength="2"
                    class="date-input-month"
                    placeholder=" -- "
                    @input=${this._monthChanged}
                    @focus=${this._focus}
                    @click=${this._focus}
                    @mouseup=${this._mouseUp}
                    @blur=${this._blur}
                    @keydown=${this._keydown}
                    @change=${(e: Event) => e.stopPropagation()}
                    .defaultValue=${this._getDatePart(this.defaultValue, "month")}
                    ?disabled=${this.disabled || this.disableMonth}
                    ?readonly=${this.readonly}
                />

                <div>.</div>

                <input
                    type="number"
                    pattern="[0-9]*"
                    inputmode="numeric"
                    maxlength="4"
                    class="date-input-year"
                    placeholder=" ---- "
                    max="2100"
                    @input=${this._yearChanged}
                    @focus=${this._focus}
                    @click=${this._focus}
                    @mouseup=${this._mouseUp}
                    @blur=${this._blur}
                    @keydown=${this._keydown}
                    @change=${(e: Event) => e.stopPropagation()}
                    .defaultValue=${this._getDatePart(this.defaultValue, "year")}
                    ?disabled=${this.disabled || this.disableYear}
                    ?readonly=${this.readonly}
                />

                <input
                    type="date"
                    .name=${this.name}
                    .min=${this.min || "1900-01-01"}
                    .max=${this.max || "2100-01-01"}
                    ?required=${this.required}
                    tabindex="-1"
                />
            </div>

            ${this.datePicker === "inline"
                ? html`
                      <ptc-date-picker
                          class="smaller border-top"
                          @change=${this._datePicked}
                          @click=${(e: Event) => e.stopPropagation()}
                          .min=${this.min}
                          .max=${this.max}
                          .displayRangeWith=${this.displayRangeWith}
                          .badge=${this.datePickerBadge}
                      ></ptc-date-picker>
                  `
                : this.datePicker === "popover"
                  ? html`
                        <ptc-popover
                            ${ref(this._popoverElementRef)}
                            trigger="focus"
                            style="--popover-hover-buffer: 0px;"
                        >
                            <ptc-date-picker
                                class="smaller"
                                @change=${this._datePicked}
                                @click=${(e: Event) => e.stopPropagation()}
                                .min=${this.min}
                                .max=${this.max}
                                .displayRangeWith=${this.displayRangeWith}
                                .badge=${this.datePickerBadge}
                            ></ptc-date-picker>
                        </ptc-popover>
                    `
                  : ""}
        `;
    }
}
